/**
 * Auth Provider - Authentication state management
 * For Media Devoted | Created By Ciro Autiero
 *
 * Responsibilities:
 *   - Hold the current user in React state.
 *   - Call `/auth/me` to hydrate on mount / after login.
 *   - React to `trafficpulse:auth-cleared` (fired by `api.ts` when the
 *     refresh token is rejected) by wiping React state.
 *
 * Importantly, this provider does NOT run its own logout timer anymore: the
 * token lifecycle lives entirely in `api.ts`, which silently refreshes
 * tokens in the background. That's why the user no longer gets kicked out
 * while actively working.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import {
  api,
  setAuthTokens,
  clearAuthAndRedirect,
  TOKEN_KEY,
} from '@/services/api';

interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
  permissions: string[];
}

function normalizeUser(data: Record<string, unknown>): User {
  if (data.role && typeof data.role === 'object') {
    const roleObj = data.role as {
      name: string;
      permissions: { code?: string }[] | string[];
    };
    return {
      id: data.id as string,
      email: data.email as string,
      firstName: data.firstName as string | undefined,
      lastName: data.lastName as string | undefined,
      role: roleObj.name,
      permissions: roleObj.permissions
        .map((p) => (typeof p === 'string' ? p : p.code || ''))
        .filter(Boolean),
    };
  }

  return {
    id: data.id as string,
    email: data.email as string,
    firstName: data.firstName as string | undefined,
    lastName: data.lastName as string | undefined,
    role: data.role as string,
    permissions: (data.permissions as string[]) || [],
  };
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  logout: () => void;
  refreshMe: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const refreshMe = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(null);
      return;
    }
    const response = await api.get('/auth/me');
    setUser(normalizeUser(response.data.data));
  }, []);

  // React to session termination triggered by `api.ts` (refresh rejected, 401
  // with no refresh token, explicit logout, etc.). We never own the
  // redirect — `clearAuthAndRedirect` handles that — we just clear React
  // state so the UI is consistent.
  useEffect(() => {
    const onAuthCleared = () => {
      setUser(null);
      queryClient.clear();
    };
    window.addEventListener('trafficpulse:auth-cleared', onAuthCleared);
    return () => {
      window.removeEventListener('trafficpulse:auth-cleared', onAuthCleared);
    };
  }, [queryClient]);

  // Bootstrap on mount: if we have a token, hydrate `user`. Any 401 here is
  // handled by the axios response interceptor (silent refresh), so this only
  // throws when the session is truly dead — in which case `api.ts` has
  // already redirected to /login.
  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) {
        try {
          await refreshMe();
        } catch {
          if (!cancelled) {
            setUser(null);
          }
        }
      }
      if (!cancelled) setIsLoading(false);
    };
    void init();
    return () => {
      cancelled = true;
    };
  }, [refreshMe]);

  const login = useCallback(
    async (email: string, password: string, rememberMe = false) => {
      const response = await api.post('/auth/login', {
        email,
        password,
        rememberMe,
      });

      const {
        user: userData,
        accessToken,
        refreshToken,
        expiresIn,
      } = response.data.data;

      setAuthTokens(accessToken, refreshToken, expiresIn || 900);
      setUser(normalizeUser(userData));

      navigate('/dashboard');
    },
    [navigate],
  );

  const logout = useCallback(() => {
    // Delegates to `api.ts` so the proactive refresh timer + header +
    // storage are all cleaned up in one place. It dispatches
    // `trafficpulse:auth-cleared`, which will clear React state.
    clearAuthAndRedirect();
  }, []);

  const hasPermission = useCallback(
    (permission: string): boolean => {
      if (!user?.permissions) return false;
      if ((user.role || '').toLowerCase() === 'admin') return true;
      return user.permissions.includes(permission);
    },
    [user],
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        refreshMe,
        hasPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
